import React, { useState } from 'react';
import {
  Content,
  Page,
  Header,
  ContentHeader,
  Progress,
  ErrorPanel,
  Avatar,
} from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import {
  Grid,
  makeStyles,
  Paper,
  TextField,
  IconButton,
  Typography,
  Tooltip,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import PsychologyIcon from '@material-ui/icons/Smartphone';
interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const WANTED_URL = "api/proxy/wanted"

const useStyles = makeStyles(theme => ({
  chatContainer: {
    height: 'calc(100vh - 250px)',
    display: 'flex',
    flexDirection: 'column',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(2),
    maxWidth: '70%',
  },
  userMessage: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    alignSelf: 'flex-end',
  },
  assistantMessage: {
    backgroundColor: theme.palette.background.paper,
    alignSelf: 'flex-start',
  },
  inputContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: '80px',
    maxHeight: '200px',
  },
  input: {
    flex: 1,
    minHeight: '80px',
    maxHeight: '200px',
    overflowY: 'auto',
    fontSize: '1.1rem',
    padding: theme.spacing(1.5),
  },
  sendButton: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  icon: {
    fontSize: '28px',
    margin: '0 14px',
    color: theme.palette.text.primary,
  },
}));

export const ChatPage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [userId, setUserId] = useState<string>('anonymous');
  const [userEntity, setUserEntity] = useState<any>(null);

  React.useEffect(() => {
    const fetchIdentity = async () => {
      try {
        const { userEntityRef } = await identityApi.getBackstageIdentity();
        setUserId(userEntityRef.split('/').pop() || 'anonymous');
        
        // Try to get the user entity if available
        try {
          const entity = await identityApi.getProfileInfo();
          setUserEntity(entity);
        } catch (profileErr) {
          console.error('Failed to fetch user profile:', profileErr);
        }
      } catch (err) {
        console.error('Failed to fetch user identity:', err);
      }
    };
    fetchIdentity();
  }, [identityApi]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const { token } = await identityApi.getCredentials();
    if (!token) {
      throw new Error('No authentication token found.');
    }
  
    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(undefined);

    try {
      const response = await fetch(`${WANTED_URL}/rag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userprompt: input,
          messages: messages,
          userid: userId,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
  
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is empty.');
      }
  
      let assistantMessage = '';
      const decoder = new TextDecoder();
  
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
  
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        assistantMessage += chunk;
        
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessageIndex = newMessages.length - 1;
  
          if (newMessages[lastMessageIndex].role === 'assistant') {
            newMessages[lastMessageIndex] = {
              ...newMessages[lastMessageIndex],
              content: assistantMessage,
            };
          }
  
          return [...newMessages];
        });
      }
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to connect to AI service'));
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error connecting to the AI service. Please try again later.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Wanted" subtitle="Ask questions and get instant answers from our knowledge database" />
      <Content>
        <ContentHeader title="" />
        {error && (
          <ErrorPanel
            error={error}
            title="Error communicating with AI service"
            defaultExpanded
          />
        )}
        <Grid container>
          <Grid item xs={12}>
            <Paper className={classes.chatContainer}>
              <div className={classes.messageList}>
                {messages.map((message, index) => (
                  <Grid 
                    key={index} 
                    container 
                    spacing={1} 
                    justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'} 
                    alignItems="center"
                  >
                    {message.role === 'assistant' && (
                      <PsychologyIcon className={classes.icon} />
                    )}
                    <Paper
                      className={`${classes.message} ${
                        message.role === 'user' ? classes.userMessage : classes.assistantMessage
                      }`}
                    >
                      <Typography>{message.content}</Typography>
                    </Paper>
                    {message.role === 'user' && (
                      userEntity ? (
                        <Avatar 
                          {...userEntity}
                          className={classes.icon}
                        />
                      ) : (
                        <AccountCircleIcon className={classes.icon} />
                      )
                    )}
                  </Grid>
                ))}
                {isLoading && <Progress />}
              </div>
              <div className={classes.inputContainer}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs>
                    <TextField
                      className={classes.input}
                      variant="outlined"
                      placeholder="Type your message..."
                      aria-label="Chat message input"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      multiline
                      maxRows={4}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item>
                    <Tooltip title="Send message" placement="top">
                      <span>
                        <IconButton
                          className={classes.sendButton}
                          color="primary"
                          onClick={sendMessage}
                          disabled={isLoading || !input.trim()}
                          aria-label="Send message"
                        >
                          <SendIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
